import React from 'react'

import PropTypes from 'prop-types'
import { Grid, Box, styled } from '@mui/material'
import StrapiLink from '@templates/strapi-cms/content-types/Link'
import { useTest } from '@hooks/useTest'
import RightSideBanners from './RightSideBanners'
import RightSideBanner from './RightSideBanner'

const GridWrapper = styled('div')`
  display: grid;
  grid-template-columns: 67.4% 32.6%;
  gap: 0;
  margin: 16px auto 0;
  width: 100%;
  padding: 0 16px;
  max-width: 1440px;
  max-height: calc(100vh - 160px);
  min-height: min(407px, calc(100vh - 160px));
  .link {
    color: #333;
    text-decoration: none;
    display: block;
    line-height: normal;
    padding-bottom: 5px;
  }

  .clickable {
    &:hover,
    &:focus {
      color: ${p => p.theme.palette.primary.main};
      text-decoration: underline;
      outline: none !important;
    }
    & .link-arrow-next {
      top: 46%;
    }
  }

  .subcategory {
    font-size: 14px;
    line-height: 18px;
  }

  .heading {
    font-size: 16px;
    font-weight: 600;
    width: fit-content;
  }
`

const LeftSide = styled(Grid)`
  display: flex;
  flex-wrap: wrap;
  overflow-y: auto;
`

const CategoryList = styled('ul')`
  list-style: none;
  padding: 0;
  margin: 0;
  & > a {
    width: fit-content;
  }
`

const Megamenu = ({ data, onClose }) => {
  const navLinks = data?.NavLinks
  const bannerGroup = data?.LeftSideCategoryBanners?.NavigationBannerGroup

  // We'll trigger the new design and spacing if the content team includes a single banner
  const hasOneBanner = bannerGroup?.CategoryBanners?.length === 1

  const { generateTestId } = useTest()

  /* per Janis, use auto column flow for living room category only -- everything else uses balance so the content is spread evenly */
  /* FD-2818 - fix for broken auto in Chrome and Firefo */
  // const columnFill = Object.is(data?.PrimaryLink?.Link?.DisplayText.toLowerCase(), 'living rooms') ? 'auto' : 'balance'
  const columnFill = data?.NavLinksSingleColumn ? 'auto' : 'balance'

  const columnCount = bannerGroup ? 3 : 5

  const analyticsData = Link => ({
    event: 'navigation_link',
    nav_type: 'desktop',
    nav_link_list: 'Subcategory Link',
    nav_section: `Nav Links : ${Link?.DisplayText}`,
    nav_link: Link?.DisplayText,
    nav_route: Link?.InternalUrl || '',
  })

  return (
    <GridWrapper>
      <LeftSide sx={{ gridColumn: bannerGroup ? '1 / 2' : '1 / 3', gridRow: '1 / 2' }}>
        <Box
          style={{
            columnCount: `${columnCount}`,
            columnGap: 20,
            paddingRight: 16,
            columnFill,
            width: '100%',
          }}
        >
          {navLinks?.map?.(({ LinkList }, index) => (
            <Box
              role="group"
              aria-labelledby={`heading-${LinkList?.id}`}
              key={LinkList?.id}
              style={{ paddingBottom: 20, breakInside: 'avoid' }}
            >
              {LinkList?.HeadingLinkClickable ? (
                <StrapiLink
                  className="link heading clickable"
                  data-textid={LinkList?.HeadingLink?.Link?.id}
                  data={LinkList?.HeadingLink}
                  onClick={onClose}
                  arrowNext
                  trackingData={{
                    category: 'navigation',
                    action: 'link click',
                    label: LinkList?.HeadingLink?.Link?.Title || LinkList?.HeadingLink?.Link?.DisplayText,
                    ...analyticsData(LinkList?.HeadingLink?.Link),
                  }}
                />
              ) : (
                <h3
                  id={`heading-${LinkList?.id}`}
                  className="heading link"
                  style={{ color: LinkList?.HeadingLink?.Link?.Color?.ColorHex ?? '#333' }}
                  data-testid={generateTestId('navigation', 'sub-category')}
                >
                  {LinkList?.HeadingLink?.Link?.DisplayText}
                </h3>
              )}

              <CategoryList
                className="category-list"
                aria-label={`${LinkList?.HeadingLink?.Link?.DisplayText} subcategories`}
              >
                {LinkList?.Links?.map?.(({ Link }) => (
                  <StrapiLink
                    key={Link?.id}
                    data-textid={Link?.id}
                    className="link subcategory clickable"
                    data={Link}
                    onClick={onClose}
                    trackingData={{
                      category: 'navigation',
                      action: 'link click',
                      label: Link?.Title || Link?.DisplayText,
                      ...analyticsData(Link),
                    }}
                  />
                ))}
              </CategoryList>
            </Box>
          ))}
        </Box>
      </LeftSide>
      {!hasOneBanner && <RightSideBanners bannerGroup={bannerGroup} onClose={onClose} />}
      {hasOneBanner && <RightSideBanner bannerGroup={bannerGroup} onClose={onClose} />}
    </GridWrapper>
  )
}

Megamenu.propTypes = {
  data: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default Megamenu
